/**
 * LO COMÚN DE «MIS NEGOCIOS» (2026-10-03).
 *
 * Lo pidió el dueño: «a mis negocios hazlo parecido a lo que hicimos en mis
 * sucursales, porque allí también le faltó chicha». Las pantallas —Resumen,
 * Ventas, Dinero e Inventario— usan las piezas de «Todas las sucursales» (el
 * período, los colores, las barras repartidas), y aquí vive lo que es propio
 * de mirar varios negocios: la moneda de cada uno y cómo se entra en él.
 *
 * ⚠️ Todo el apartado trata de usted, como «Todas las sucursales».
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, Info, Lock } from "lucide-react";
import { Boton, Nota, Vacio } from "@/components/ui";
import { SoftfiError } from "@/lib/api";
import { MONEDA_POR_DEFECTO, type FormatoMoneda } from "@/lib/money";
import { useAuth } from "@/stores/auth";
import { colorDe, useFormato } from "@/features/sucursales/comun";

/** `{negocio: color}`, en el orden en que llegan: el actual primero. */
export function coloresDeNegocios(negocios: { tenant_id: string }[]): Map<string, string> {
  return new Map(negocios.map((n, i) => [n.tenant_id, colorDe(i)]));
}

/**
 * El formato de la moneda de cada negocio.
 *
 * ⚠️ Cada negocio puede cobrar en una moneda distinta: pintar bolívares con el
 * símbolo del dólar sería el error que hace que nadie se fíe de la pantalla.
 * La del negocio desde el que se mira usa su formato completo.
 */
export function useFormatoDe(): (moneda: string) => FormatoMoneda {
  const formato = useFormato();
  return (moneda) => {
    if (moneda === formato.code) return formato;
    if (moneda === "USD") return MONEDA_POR_DEFECTO;
    if (moneda === "VES") return { code: "VES", symbol: "Bs", decimals: 2, symbol_before: false };
    return { code: moneda, symbol: moneda, decimals: 2, symbol_before: false };
  };
}

/**
 * Entrar en un negocio y aterrizar en una de sus pantallas.
 *
 * ⚠️ Las dos cosas juntas y en ese orden: llevarlo a la pantalla sin cambiar
 * de negocio le enseñaría lo de OTRO, que es peor que no llevarlo.
 */
export function useIrAlNegocio() {
  const { tenant, cambiarNegocio } = useAuth();
  const navegar = useNavigate();
  const [yendo, setYendo] = useState<string | null>(null);

  async function ir(tenantId: string, destino = "/") {
    if (tenantId === tenant?.id) {
      navegar(destino);
      return;
    }
    setYendo(tenantId);
    try {
      await cambiarNegocio(tenantId);
      navegar(destino);
    } finally {
      setYendo(null);
    }
  }

  return { ir, yendo, actual: tenant?.id ?? null };
}

/** ¿El error es «esto es de Premium»? */
export function esDePremium(error: unknown): boolean {
  return error instanceof SoftfiError && error.status === 402;
}

/** Lo que se enseña en Ventas, Dinero e Inventario sin Premium. */
export function SinPremium() {
  return (
    <Vacio
      icono={<Lock size={30} />}
      titulo="Vista detallada de sus negocios"
      descripcion="Las ventas, las finanzas y el inventario de todos sus negocios, comparados lado a lado, forman parte del plan Premium. Basta con que uno de sus negocios lo tenga."
      accion={
        <Link to="/premium">
          <Boton>Conocer el plan Premium</Boton>
        </Link>
      }
    />
  );
}

/** Con un solo negocio no hay nada que juntar. */
export function SoloUnNegocio() {
  return (
    <Vacio
      icono={<Building2 size={32} />}
      titulo="Usted tiene un solo negocio"
      descripcion="Este apartado reúne la información de todos sus negocios: ventas, finanzas e inventario, cada uno con sus sucursales. Se activará automáticamente al crear un segundo negocio desde el selector de negocios."
    />
  );
}

/**
 * Lo que NO se sumó. Va arriba y no en letra pequeña: un total al que le falta
 * un negocio y no lo dice es un total que engaña.
 */
export function MonedasNoSumadas({ moneda, otras }: { moneda: string; otras: string[] }) {
  if (otras.length === 0) return null;
  return (
    <Nota tono="aviso" icono={<Info size={15} />}>
      Los totales solo suman los negocios que operan en <strong>{moneda}</strong>. Los negocios en{" "}
      {otras.join(", ")} se muestran con sus propias cifras: convertirlos a la tasa del día daría un
      total que varía con el tipo de cambio aunque no se haya vendido nada.
    </Nota>
  );
}
